import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, forwardRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { PlatformService } from 'app/core/platform/platform.service';
import { getPropertyOfObject, trackByUuid } from 'app/utils';
import { Observable, Subject, takeUntil } from 'rxjs';

export interface BaseValue {
    uuid: string;
}

@Component({
    selector: 'app-checkbox-list',
    templateUrl: 'checkbox-list.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CheckboxListComponent),
            multi: true
        }
    ],
})
export class CheckboxListComponent<T extends BaseValue> implements OnInit, OnDestroy, ControlValueAccessor {
    //@Input() filterProperties: string[];
    @Input() displayProperty: string;
    @Input() iconProperty: string;
    @Input() availableValuesObservable: Observable<T[]>;
    @Input() label: string;
    @Input() showCheckAll: boolean;
    @Input() showSearch: boolean = true;

    @ViewChild('searchInput') private searchInput: ElementRef;

    selectedValues: T[];
    filteredValues: T[];
    availableValues: T[];
    disabled: boolean;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private platformService: PlatformService,
    ) { }

    ngOnInit() {
        this.availableValuesObservable.pipe(takeUntil(this._unsubscribeAll)).subscribe(values => {
            this.availableValues = values;
            this.filteredValues = values;
            this._changeDetectorRef.markForCheck();
        });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    onChange = (_: any) => {
    };

    onTouched = () => {
    };

    setValues(values: T[], updateFromModel?: boolean) {
        this.selectedValues = values;

        if (!updateFromModel) {
            this.onChange(values);
        }
        this._changeDetectorRef.markForCheck();
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    writeValue(value: T[]): void {
        this.setValues(value, true);
    }

    setDisabledState(disabled: boolean) {
        this.disabled = disabled;
    }

    onContainerClick() {
        this.onTouched();
    }

    hasValue(value: T): boolean {
        return !!this.selectedValues?.find(valId => valId.uuid === value.uuid);
    }

    onInput(event) {
        this.filterValues(event.target.value)
    }

    filterValues(filter: string): void {
        const value = filter.toLowerCase();
        this.filteredValues = this.availableValues.filter(val => this.getDisplayProperty(val).toLowerCase().includes(value));
        this._changeDetectorRef.markForCheck();
    }

    getDisplayProperty(value: T) {
        return getPropertyOfObject(value, this.displayProperty)
    }

    hasIcon(value: T) {
        return this.iconProperty && getPropertyOfObject(value, this.iconProperty);
    }

    getIcon(value: T) {
        if (!this.iconProperty) {
            return "";
        }

        return this.platformService.getFileSrc(getPropertyOfObject(value, this.iconProperty), 'logo') ?? "";
    }

    filterValuesKeyDown(event): void {
        switch (event.key) {
            case "Enter":
                this.enterKeyPressed();
                event.preventDefault();
                event.stopPropagation();
                break;
            case "Escape":
                this.searchInput.nativeElement.value = "";
                this.filterValues("");
                event.preventDefault();
                event.stopPropagation();
                break;
        }
    }

    enterKeyPressed() {
        if (this.filteredValues.length === 0) {
            return;
        }

        const val = this.filteredValues[0];
        const hasValue = this.selectedValues.find(valId => valId.uuid === val.uuid);

        if (hasValue) {
            this.removeValue(val);
        } else {
            this.addValue(val);
        }
    }

    toggleValue(value: T, change: MatCheckboxChange): void {
        if (change.checked) {
            this.addValue(value);
        } else {
            this.removeValue(value);
        }
    }

    toggleAll(change: MatCheckboxChange) {
        if (change.checked) {
            this.setValues(this.availableValues);
        } else {
            this.setValues([]);
        }
    }

    isEverythingChecked() {
        return this.selectedValues?.length === this.availableValues?.length;
    }

    addValue(value: T): void {
        if (!this.selectedValues) {
            this.selectedValues = [];
        }
        this.setValues([...this.selectedValues, value]);
    }

    removeValue(value: T): void {
        let indexToRemove = this.selectedValues.findIndex(valId => valId.uuid === value.uuid);
        this.setValues(this.selectedValues.filter((_, i) => i !== indexToRemove))
    }

    get trackByFn() {
        return trackByUuid;
    }
}
